import React, { memo, useMemo, useRef, useEffect, useState, useCallback } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useAgentStore } from '@/stores/agentStore';
import { WelcomeScreen } from './WelcomeScreen';
import { TypewriterTextBlock, ProcessLogPanel } from './ProcessLogPanel';
import { StreamingMessage, AIReplyBlock } from './AIReplyBlock';
import { UserMessageBubble, AssistantMessageBubble } from './MessageBubble';
import { SearchResultsList, buildSearchListProps } from '../SearchResultCard';

import type { Message } from '../types';

const THINKING_TEXTS: Record<string, string> = {
  search: '正在搜索相关资料...',
  generate_image: '正在构思画面...',
  generate_video: '正在构思视频...',
  canvas_operation: '正在搭建工作流...',
  analyze_image: '正在分析图片...',
  chat: '正在思考...',
};

interface MessageListProps {
  onSkillClick: (label: string) => void;
  onGenerationConfirm: (params: any, msg: Message) => void;
  onGenerationCancel: (msg: Message) => void;
  onApplyWorkflow: (wf: any) => void;
}

const MAX_VISIBLE_MESSAGES = 50;
// 虚拟列表默认预估行高
const ESTIMATE_ROW_HEIGHT = 120;

export const MessageList: React.FC<MessageListProps> = memo(({
  onSkillClick, onGenerationConfirm, onGenerationCancel, onApplyWorkflow,
}) => {
  const {
    messages, isLoading, isThinking, thinkingSeconds, currentIntent,
    streamingText, activeAssistantIntro, activeProcessLogs,
    activeReplyBuilder,
  } = useAgentStore(useShallow((s) => ({
    messages: s.messages,
    isLoading: s.isLoading,
    isThinking: s.isThinking,
    thinkingSeconds: s.thinkingSeconds,
    currentIntent: s.currentIntent,
    streamingText: s.streamingText,
    activeAssistantIntro: s.activeAssistantIntro,
    activeProcessLogs: s.activeProcessLogs,
    activeReplyBuilder: s.activeReplyBuilder,
  })));

  const hasMessages = useMemo(() =>
    messages.length > 0 || streamingText !== null || activeProcessLogs.length > 0 || activeAssistantIntro !== null,
    [messages.length, streamingText, activeProcessLogs.length, activeAssistantIntro]
  );

  type StreamPhase = 'idle' | 'search' | 'confirm' | 'text' | 'thinking';
  const { phase, searchData, displayText, isThinkingDisplay, hasIntro, hasLogs } = useMemo(() => {
    const builder = activeReplyBuilder;
    if (builder?.type === 'search') {
      return { phase: 'search' as StreamPhase, searchData: builder.searchData, displayText: null, isThinkingDisplay: false, hasIntro: !!activeAssistantIntro, hasLogs: activeProcessLogs.length > 0 };
    }
    if (builder?.type === 'generation_confirm') {
      return { phase: 'confirm' as StreamPhase, searchData: null, displayText: null, isThinkingDisplay: false, hasIntro: !!activeAssistantIntro, hasLogs: activeProcessLogs.length > 0 };
    }
    const builderText = builder?.text || streamingText;
    if (builderText) {
      return { phase: 'text' as StreamPhase, searchData: null, displayText: builderText, isThinkingDisplay: false, hasIntro: !!activeAssistantIntro, hasLogs: activeProcessLogs.length > 0 };
    }
    if (isThinking || isLoading || activeProcessLogs.length > 0 || activeAssistantIntro) {
      return { phase: 'thinking' as StreamPhase, searchData: null, displayText: null, isThinkingDisplay: isThinking || isLoading, hasIntro: !!activeAssistantIntro, hasLogs: activeProcessLogs.length > 0 };
    }
    return { phase: 'idle' as StreamPhase, searchData: null, displayText: null, isThinkingDisplay: false, hasIntro: false, hasLogs: false };
  }, [activeReplyBuilder, streamingText, isThinking, isLoading, activeProcessLogs, activeAssistantIntro]);

  const prevPhaseRef = useRef<StreamPhase>(phase);
  const [phaseTransitionKey, setPhaseTransitionKey] = useState(0);
  useEffect(() => {
    if (prevPhaseRef.current !== phase) {
      if (prevPhaseRef.current === 'text' && phase === 'search') {
        setPhaseTransitionKey(k => k + 1);
      }
      prevPhaseRef.current = phase;
    }
  }, [phase]);

  useEffect(() => {
    const id = 'msglist-fadein-keyframes';
    if (!document.getElementById(id)) {
      const style = document.createElement('style');
      style.id = id;
      style.textContent = '@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }';
      document.head.appendChild(style);
    }
  }, []);

  const hasActiveReply = phase !== 'idle' || activeReplyBuilder !== null;

  const visibleMessages = useMemo(() =>
    messages.length > MAX_VISIBLE_MESSAGES ? messages.slice(-MAX_VISIBLE_MESSAGES) : messages,
    [messages]
  );
  const hiddenCount = messages.length - visibleMessages.length;

  // P3: 虚拟滚动
  const parentRef = useRef<HTMLDivElement>(null);

  // 构建虚拟列表行数据：包含折叠提示 + 消息列表 + 流式回复
  const virtualRows = useMemo(() => {
    const rows: Array<{ type: 'hidden'; count: number } | { type: 'message'; message: Message; index: number } | { type: 'streaming' }> = [];
    if (hiddenCount > 0) {
      rows.push({ type: 'hidden', count: hiddenCount });
    }
    visibleMessages.forEach((m, i) => {
      rows.push({ type: 'message', message: m, index: i });
    });
    if (hasActiveReply) {
      rows.push({ type: 'streaming' });
    }
    return rows;
  }, [hiddenCount, visibleMessages, hasActiveReply]);

  const virtualizer = useVirtualizer({
    count: virtualRows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (index) => {
      const row = virtualRows[index];
      if (!row) return ESTIMATE_ROW_HEIGHT;
      if (row.type === 'hidden') return 36;
      if (row.type === 'streaming') return 200;
      return ESTIMATE_ROW_HEIGHT;
    },
    overscan: 5,
  });

  // 新消息到达时自动滚动到底部
  const prevMessageCountRef = useRef(messages.length);
  useEffect(() => {
    if (messages.length > prevMessageCountRef.current) {
      virtualizer.scrollToIndex(virtualRows.length - 1, { align: 'end' });
    }
    prevMessageCountRef.current = messages.length;
  }, [messages.length, virtualizer, virtualRows.length]);

  const renderStreamingContent = () => {
    switch (phase) {
      case 'search': {
        const pendingQuery = activeReplyBuilder?.searchData?.query
          || (activeReplyBuilder?.introText && activeReplyBuilder.introText.startsWith('搜索「')
              ? activeReplyBuilder.introText.replace(/^搜索「|」的结果$/g, '')
              : null);
        const fadeInStyle: React.CSSProperties = phaseTransitionKey > 0
          ? { animation: 'fadeIn 200ms ease-out' }
          : {};
        return (
          <div key={`search-${phaseTransitionKey}`} style={fadeInStyle}>
            {hasIntro && activeAssistantIntro ? (
              <TypewriterTextBlock
                text={activeAssistantIntro}
                speed={12}
                className="text-[12px] leading-6 text-gray-300"
              />
            ) : null}
            {hasLogs ? (
              <ProcessLogPanel logs={activeProcessLogs} title="处理记录" />
            ) : null}
            {searchData ? (
              <SearchResultsList
                {...buildSearchListProps(searchData, activeReplyBuilder?.text ?? undefined)}
                skipIncomplete={true}
              />
            ) : (
              <div className="text-[12px] leading-6 text-gray-300 flex items-center gap-2 min-h-[40px]">
                <span className="inline-block h-2 w-2 rounded-full bg-sky-400 animate-pulse" />
                正在搜索{pendingQuery ? `「${pendingQuery}」相关资料` : '相关资料'}...
              </div>
            )}
          </div>
        );
      }
      case 'confirm':
        return (
          <>
            {hasIntro && activeAssistantIntro ? (
              <TypewriterTextBlock
                text={activeAssistantIntro}
                speed={12}
                className="text-[12px] leading-6 text-gray-300"
              />
            ) : null}
            {hasLogs ? (
              <ProcessLogPanel logs={activeProcessLogs} title="处理记录" />
            ) : null}
            <div className="text-[12px] leading-6 text-gray-300 flex items-center gap-2">
              <span className="inline-block h-2 w-2 rounded-full bg-amber-400 animate-pulse" />
              等待确认生成参数...
            </div>
          </>
        );
      case 'text':
        return (
          <>
            {hasIntro && activeAssistantIntro ? (
              <TypewriterTextBlock
                text={activeAssistantIntro}
                speed={12}
                className="text-[12px] leading-6 text-gray-300"
              />
            ) : null}
            {hasLogs ? (
              <ProcessLogPanel logs={activeProcessLogs} title="处理记录" />
            ) : null}
            {displayText ? <StreamingMessage text={displayText} /> : null}
          </>
        );
      case 'thinking':
        return (
          <>
            {hasIntro && activeAssistantIntro ? (
              <TypewriterTextBlock
                text={activeAssistantIntro}
                speed={12}
                className="text-[12px] leading-6 text-gray-300"
              />
            ) : null}
            {hasLogs ? (
              <ProcessLogPanel logs={activeProcessLogs} title="处理记录" />
            ) : null}
            {!hasIntro && !hasLogs ? (
              <div className="text-[12px] leading-6 text-gray-300">
                {THINKING_TEXTS[currentIntent] || '正在思考...'}
              </div>
            ) : null}
          </>
        );
      case 'idle':
      default:
        return null;
    }
  };

  if (!hasMessages && !isLoading) {
    return <WelcomeScreen onSkillClick={onSkillClick} />;
  }

  return (
    <div ref={parentRef} className="flex flex-col gap-5 pt-4 pb-2 h-full" style={{ overflowY: 'auto' }}>
      <div
        style={{
          height: virtualizer.getTotalSize(),
          width: '100%',
          position: 'relative',
        }}
      >
        {virtualizer.getVirtualItems().map((virtualItem) => {
          const row = virtualRows[virtualItem.index];
          if (!row) return null;

          return (
            <div
              key={virtualItem.key}
              data-index={virtualItem.index}
              ref={virtualizer.measureElement}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualItem.start}px)`,
              }}
            >
              {row.type === 'hidden' && (
                <div className="text-center text-xs text-slate-500 py-2">
                  ↑ {row.count} 条更早的消息已折叠
                </div>
              )}
              {row.type === 'message' && (
                <div className={`flex w-full py-2.5 ${row.message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  {row.message.role === 'user' ? (
                    <UserMessageBubble message={row.message} />
                  ) : (
                    <AssistantMessageBubble
                      message={row.message}
                      isHistory={row.message.isHistory}
                      onGenerationConfirm={onGenerationConfirm}
                      onGenerationCancel={onGenerationCancel}
                      onApplyWorkflow={onApplyWorkflow}
                    />
                  )}
                </div>
              )}
              {row.type === 'streaming' && (
                <div className="flex justify-start w-full">
                  <AIReplyBlock
                    isStreaming={phase !== 'idle'}
                    isThinking={isThinkingDisplay || phase === 'thinking'}
                    thinkingSeconds={isThinkingDisplay ? thinkingSeconds : undefined}
                    content={<div className="space-y-3">{renderStreamingContent()}</div>}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
});
